import { t, useT } from "@/lib/i18n";
import { WriteOnly } from "@/components/auth/write-only";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useBrandAnswer } from "@fox1ck-org/auth/saas/react";
import { Pencil, Plus, Trash2, Workflow } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "@fox1ck-org/ui/toast";
import { ListView } from "@/components/list-view";
import { Button, PageHeader, SearchField } from "@fox1ck-org/ui";
import { PageState } from "@fox1ck-org/ui";
import { failureText } from "@/lib/errors";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { useArchiveFlow, useListFlows } from "@/lib/services/flow-service";
import { listSort } from "@/lib/services/catalog";
import { useDebounced } from "@/lib/hooks/use-debounced";
import { Flow, FlowMode } from "@gen/tracker/v1/flow_pb";

export function flowModeLabel(mode: FlowMode): string {
  switch (mode) {
    case FlowMode.SINGLE_OFFER:
      return t("flows.mode.single");
    case FlowMode.SPLIT_TEST:
      return t("flows.mode.split");
    case FlowMode.WEIGHTED_ROTATOR:
      return t("flows.mode.weighted");
    case FlowMode.RULE_BASED:
      return t("flows.mode.rules");
    case FlowMode.TEMPLATE_DERIVED:
      return t("flows.mode.template");
    default:
      return "—";
  }
}

export default function FlowsListPage() {
  const navigate = useNavigate();
  const tr = useT();
  const { motif } = useBrandAnswer();
  const params = useTableParams({ defaultSort: [{ id: "name", desc: false }] });
  const search = params.search;
  // Every flow is searched, ordered and paged by the server (one per campaign
  // — hundreds of them); the table shows its page and the true total.
  const q = useDebounced(search.trim());
  const { data, isLoading, error, refetch } = useListFlows({
    search: q,
    sort: listSort(params.sorting, { name: "name" }),
    limit: params.pageSize,
    offset: params.pageIndex * params.pageSize,
  });
  const items = data?.items ?? [];
  const archive = useArchiveFlow();

  const columns = useMemo<ColumnDef<Flow>[]>(
    () => [
      {
        id: "name",
        header: tr("flows.col.name"),
        accessorKey: "name",
        cell: ({ row }) => (
          <button
            type="button"
            className="flex items-center gap-2 text-left text-foreground hover:text-primary"
            onClick={() => navigate(`/admin/flows/${row.original.id}/edit`)}
          >
            <Workflow className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
            <span>{row.original.name}</span>
          </button>
        ),
        size: 360,
      },
      {
        id: "mode",
        header: tr("flows.col.mode"),
        enableSorting: false,
        accessorFn: (r) => flowModeLabel(r.mode),
        size: 140,
      },
      {
        id: "nodes",
        header: tr("flows.col.nodes"),
        enableSorting: false,
        accessorFn: (r) => r.nodes.length,
        cell: ({ row }) => (
          <span className="tabular-nums text-muted-foreground">
            {tr("flows.nodesEdges", { nodes: row.original.nodes.length, edges: row.original.edges.length })}
          </span>
        ),
        size: 160,
      },
      {
        id: "actions",
        header: tr("flows.col.actions"),
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 px-0"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/flows/${row.original.id}/edit`);
              }}
              title={tr("flows.edit")}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 px-0"
              onClick={(e) => {
                e.stopPropagation();
                archive.mutate(row.original.id, {
                  onSuccess: () => toast.success(tr("flows.archived")),
                  onError: (err) => toast.error(err instanceof Error ? err.message : tr("flows.archiveFailed")),
                });
              }}
              title={tr("flows.archive")}
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
        ),
        size: 120,
      },
    ],
    [navigate, archive, tr],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-col gap-3 px-5 pb-3 pt-4">
        <PageHeader
          title={tr("flows.title")}
          icon={<Workflow className="size-4" strokeWidth={1.75} />}
          subtitle={tr("flows.subtitle")}
          actions={
            <WriteOnly>
              <Button variant="primary" onClick={() => navigate("/admin/flows/new")}>
                <Plus className="size-4" /> {tr("flows.new")}
              </Button>
            </WriteOnly>
          }
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pb-5">
        {error ? (
          <PageState
            mode="error"
            title={tr("flows.loadError")}
            error={failureText(error)}
            retryLabel={t("lib.common.retry")}
            retry={() => refetch()}
          />
        ) : (
          <ListView
            columns={columns}
            data={items}
            getRowId={(row) => row.id}
            loading={isLoading}
            manualSorting
            sorting={params.sorting}
            onSortingChange={params.onSortingChange}
            pagination={{
              pageIndex: params.pageIndex,
              pageSize: params.pageSize,
              totalCount: data?.total ?? 0,
              onPageIndexChange: params.setPageIndex,
              onPageSizeChange: params.setPageSize,
              pageSizeOptions: params.pageSizeOptions,
            }}
            toolbarLeft={
              <SearchField
                value={search}
                onChange={params.setSearch}
                label={tr("flows.search")}
                placeholder={tr("flows.search.ph")}
                className="w-64"
              />
            }
            emptyState={
              search ? undefined : (
                <PageState
                  mode="empty"
                  motif={motif}
                  icon={<Workflow className="size-5" strokeWidth={1.5} />}
                  title={tr("flows.empty.title")}
                  description={tr("flows.empty.body")}
                  action={
                    <div className="flex items-center gap-2">
                      <Button variant="primary" onClick={() => navigate("/admin/flow-templates")}>
                        {tr("flows.empty.templates")}
                      </Button>
                      <Button variant="secondary" onClick={() => navigate("/admin/flows/new")}>
                        {tr("flows.empty.blank")}
                      </Button>
                    </div>
                  }
                />
              )
            }
            emptyMessage={tr("flows.noMatch")}
          />
        )}
      </div>
    </div>
  );
}
